// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useEffect, useState } from 'react';
import {
  Button,
  FileUploaderDropContainer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  ToastNotification,
} from '@carbon/react';
import { useFlagOn } from '../../shared/featureFlags';
import StatusTag from '../components/StatusTag';
import RunDetailPanel, { type Run } from '../components/RunDetailPanel';
import styles from './BulkImportPage.module.css';

/**
 * Static visual scaffold — every run below is example content for the
 * mockup, not data from any pipeline service (see admin-center/README.md).
 * Wiring this to a real bulk-import/OCR pipeline is a follow-up PR.
 */
const INITIAL_RUNS: Run[] = [
  {
    document: 'New-Upload-Demo.pdf',
    site: 'demo',
    rev: '—',
    started: '8/6/2026, 11:02:14 AM',
    status: 'Completed',
    ready: 113,
    sme: 39,
    rejected: 15,
  },
  {
    document: 'Legacy-Generator-Manual-Scan.pdf',
    site: 'demo',
    rev: '—',
    started: '8/6/2026, 10:42:00 AM',
    status: 'Failed',
    message: 'OCR confidence too low — needs manual re-scan',
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/6/2026, 10:15:08 AM',
    status: 'Completed',
    ready: 149,
    sme: 59,
    rejected: 6,
  },
  {
    document: '160.78-CL1',
    site: 'demo',
    rev: '—',
    started: '8/6/2026, 9:51:01 AM',
    status: 'Completed',
    ready: 6,
    sme: 9,
    rejected: 3,
  },
  {
    document: '160.80-PW7',
    site: 'demo',
    rev: '—',
    started: '8/6/2026, 8:03:46 AM',
    status: 'Completed',
    ready: 17,
    sme: 28,
    rejected: 10,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/6/2026, 7:55:05 AM',
    status: 'Completed',
    ready: 145,
    sme: 64,
    rejected: 5,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/6/2026, 7:12:20 AM',
    status: 'Completed',
    ready: 134,
    sme: 74,
    rejected: 7,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/6/2026, 3:34:17 AM',
    status: 'Completed',
    ready: 116,
    sme: 90,
    rejected: 5,
  },
  {
    document: '160.78-CL1',
    site: 'demo',
    rev: '—',
    started: '8/6/2026, 2:53:05 AM',
    status: 'Completed',
    ready: 7,
    sme: 9,
    rejected: 2,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/6/2026, 1:17:53 AM',
    status: 'Completed',
    ready: 99,
    sme: 112,
    rejected: 7,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 11:44:59 PM',
    status: 'Completed',
    ready: 100,
    sme: 112,
    rejected: 9,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 11:24:38 PM',
    status: 'Completed',
    ready: 83,
    sme: 122,
    rejected: 7,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 10:41:31 PM',
    status: 'Completed',
    ready: 96,
    sme: 112,
    rejected: 10,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 10:17:32 PM',
    status: 'Completed',
    ready: 100,
    sme: 113,
    rejected: 5,
  },
  {
    document: 'Global-Enterprise-FDD-Rule-Library-Rev-19',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 9:39:22 PM',
    status: 'Completed',
    ready: 47,
    sme: 296,
    rejected: 127,
  },
  {
    document: '160.80-PW7',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 9:02:47 PM',
    status: 'Completed',
    ready: 22,
    sme: 34,
    rejected: 8,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 8:18:15 PM',
    status: 'Completed',
    ready: 121,
    sme: 88,
    rejected: 6,
  },
  {
    document: '160.78-CL1',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 7:47:02 PM',
    status: 'Completed',
    ready: 9,
    sme: 11,
    rejected: 4,
  },
  {
    document: 'Global-Enterprise-FDD-Rule-Library-Rev-19',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 7:05:39 PM',
    status: 'Completed',
    ready: 52,
    sme: 281,
    rejected: 119,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 6:22:10 PM',
    status: 'Completed',
    ready: 108,
    sme: 95,
    rejected: 8,
  },
  {
    document: 'Legacy-Generator-Manual-Scan.pdf',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 5:41:53 PM',
    status: 'Failed',
    message: 'Password-protected file — remove protection and retry',
  },
  {
    document: '160.80-PW7',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 5:03:26 PM',
    status: 'Completed',
    ready: 19,
    sme: 25,
    rejected: 6,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 4:19:07 PM',
    status: 'Completed',
    ready: 142,
    sme: 70,
    rejected: 9,
  },
  {
    document: '160.78-CL1',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 3:38:44 PM',
    status: 'Completed',
    ready: 8,
    sme: 10,
    rejected: 3,
  },
  {
    document: 'Global-Enterprise-FDD-Rule-Library-Rev-19',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 2:57:12 PM',
    status: 'Completed',
    ready: 61,
    sme: 274,
    rejected: 103,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 2:11:39 PM',
    status: 'Completed',
    ready: 128,
    sme: 81,
    rejected: 11,
  },
  {
    document: '160.80-PW7',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 1:26:04 PM',
    status: 'Completed',
    ready: 15,
    sme: 30,
    rejected: 12,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 12:44:31 PM',
    status: 'Completed',
    ready: 110,
    sme: 93,
    rejected: 7,
  },
  {
    document: '160.78-CL1',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 11:58:15 AM',
    status: 'Completed',
    ready: 6,
    sme: 8,
    rejected: 2,
  },
  {
    document: 'New-Upload-Demo.pdf',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 11:12:47 AM',
    status: 'Completed',
    ready: 89,
    sme: 44,
    rejected: 13,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 10:29:20 AM',
    status: 'Completed',
    ready: 137,
    sme: 76,
    rejected: 6,
  },
  {
    document: 'Global-Enterprise-FDD-Rule-Library-Rev-19',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 9:44:58 AM',
    status: 'Completed',
    ready: 44,
    sme: 302,
    rejected: 141,
  },
  {
    document: '160.80-PW7',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 9:01:33 AM',
    status: 'Completed',
    ready: 21,
    sme: 27,
    rejected: 9,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 8:17:06 AM',
    status: 'Completed',
    ready: 119,
    sme: 89,
    rejected: 5,
  },
  {
    document: 'Legacy-Generator-Manual-Scan.pdf',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 7:32:41 AM',
    status: 'Failed',
    message: 'Unsupported file format — convert to PDF and retry',
  },
  {
    document: '160.78-CL1',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 6:49:19 AM',
    status: 'Completed',
    ready: 7,
    sme: 9,
    rejected: 3,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 6:03:52 AM',
    status: 'Completed',
    ready: 104,
    sme: 98,
    rejected: 10,
  },
  {
    document: 'Global-Enterprise-FDD-Rule-Library-Rev-19',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 5:18:27 AM',
    status: 'Completed',
    ready: 58,
    sme: 288,
    rejected: 112,
  },
  {
    document: '160.80-PW7',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 4:34:01 AM',
    status: 'Completed',
    ready: 18,
    sme: 31,
    rejected: 7,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 3:49:36 AM',
    status: 'Completed',
    ready: 131,
    sme: 84,
    rejected: 8,
  },
  {
    document: '160.78-CL1',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 3:05:12 AM',
    status: 'Completed',
    ready: 9,
    sme: 12,
    rejected: 2,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 2:20:44 AM',
    status: 'Completed',
    ready: 114,
    sme: 91,
    rejected: 6,
  },
  {
    document: 'Global-Enterprise-FDD-Rule-Library-Rev-19',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 1:36:19 AM',
    status: 'Completed',
    ready: 49,
    sme: 296,
    rejected: 128,
  },
  {
    document: '160.80-PW7',
    site: 'demo',
    rev: '—',
    started: '8/5/2026, 12:51:53 AM',
    status: 'Completed',
    ready: 23,
    sme: 29,
    rejected: 10,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/4/2026, 11:58:27 PM',
    status: 'Completed',
    ready: 122,
    sme: 86,
    rejected: 7,
  },
  {
    document: '160.78-CL1',
    site: 'demo',
    rev: '—',
    started: '8/4/2026, 11:13:04 PM',
    status: 'Completed',
    ready: 8,
    sme: 9,
    rejected: 3,
  },
  {
    document: 'New-Upload-Demo.pdf',
    site: 'demo',
    rev: '—',
    started: '8/4/2026, 10:28:39 PM',
    status: 'Completed',
    ready: 95,
    sme: 41,
    rejected: 14,
  },
  {
    document: 'Airside-Fault-Guide-v1.2',
    site: 'demo',
    rev: '—',
    started: '8/4/2026, 9:44:12 PM',
    status: 'Completed',
    ready: 139,
    sme: 78,
    rejected: 6,
  },
  {
    document: 'Global-Enterprise-FDD-Rule-Library-Rev-19',
    site: 'demo',
    rev: '—',
    started: '8/4/2026, 8:59:47 PM',
    status: 'Completed',
    ready: 53,
    sme: 290,
    rejected: 117,
  },
  {
    document: '160.80-PW7',
    site: 'demo',
    rev: '—',
    started: '8/4/2026, 8:15:21 PM',
    status: 'Completed',
    ready: 16,
    sme: 33,
    rejected: 9,
  },
];

const TOAST_TIMEOUT_MS = 3000;

/**
 * Visual scaffold for Procedures > Bulk import ("Pipeline" in the mockup):
 * a dropzone plus a run history table. Dropping a file is still a no-op
 * (see admin-center/README.md); Retry/Cancel are wired to local state only
 * — no real OCR/extraction pipeline exists yet to actually retry against —
 * but the interaction itself (state transition, toast, button swap) is the
 * real production behavior, not a decorative placeholder.
 */
export default function BulkImportPage() {
  const uploadEnabled = useFlagOn('bulkImportUploadEnabled');
  const [runs, setRuns] = useState<Run[]>(INITIAL_RUNS);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const selectedRun = selectedIndex !== null ? runs[selectedIndex] : null;

  // Ticks every Running row's progress forward, same as a real client
  // polling an extraction job's status would — there's just no real job
  // behind it. A row that reaches 100% becomes a completed run with a
  // synthesized ready/sme/rejected split, the same shape every other
  // completed row already has, rather than sitting at 100% forever.
  useEffect(() => {
    const hasRunning = runs.some(r => r.status === 'Running');
    if (!hasRunning) return;

    const timer = setInterval(() => {
      // Computed from `runs` in this closure (not a setRuns functional
      // updater) precisely so it stays pure and single-invocation-safe:
      // React 18 StrictMode double-invokes updater functions to surface
      // exactly this class of bug, and an impure one (Math.random, mutating
      // an outer `anyCompleted` flag) can report a false completion toast
      // for a duplicate invocation whose result never actually committed.
      // Safe to read `runs` directly here since this effect re-subscribes
      // whenever `runs` changes (see the dependency array below).
      let anyCompleted = false;
      const nextRuns = runs.map((r): Run => {
        if (r.status !== 'Running') return r;
        const nextProgress = Math.min(100, r.progress + 8 + Math.floor(Math.random() * 13));
        if (nextProgress < 100) return { ...r, progress: nextProgress };

        anyCompleted = true;
        const total = 20 + Math.floor(Math.random() * 140);
        const rejected = Math.max(1, Math.round(total * (0.03 + Math.random() * 0.07)));
        const sme = Math.max(1, Math.round(total * (0.15 + Math.random() * 0.25)));
        return {
          document: r.document,
          site: r.site,
          rev: r.rev,
          started: r.started,
          status: 'Completed',
          ready: Math.max(0, total - rejected - sme),
          sme,
          rejected,
        };
      });
      setRuns(nextRuns);
      if (anyCompleted) setToast('Run completed.');
    }, 700);

    return () => clearInterval(timer);
  }, [runs]);

  function handleRetry(index: number) {
    setRuns(prev =>
      prev.map(
        (r, i): Run =>
          i === index
            ? {
                document: r.document,
                site: r.site,
                rev: r.rev,
                started: r.started,
                status: 'Running',
                progress: 1 + Math.floor(Math.random() * 10),
                // Carried so a Cancel below can restore the real failure
                // reason instead of fabricating a new one.
                previousMessage: r.status === 'Failed' ? r.message : undefined,
              }
            : r
      )
    );
    setToast('Retrying run.');
  }

  function handleCancel(index: number) {
    setRuns(prev =>
      prev.map(
        (r, i): Run =>
          i === index && r.status === 'Running'
            ? {
                document: r.document,
                site: r.site,
                rev: r.rev,
                started: r.started,
                status: 'Failed',
                message: r.previousMessage ?? 'Retry cancelled',
              }
            : r
      )
    );
    setToast('Run cancelled.');
  }

  return (
    <div className={styles.layout}>
      <div className={styles.main}>
        <div className={styles.title}>Pipeline</div>

        {uploadEnabled && (
          <div className={styles.dropzone}>
            <FileUploaderDropContainer
              labelText={
                'Drop a document here to start a run for demo\n' +
                'or click to choose a file — PDF, Word, Excel, PowerPoint, text'
              }
              accept={['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt']}
            />
          </div>
        )}

        <div className={styles.resultCount}>{runs.length} results</div>

        <TableContainer>
          <Table className={styles.table} size="sm">
            <TableHead>
              <TableRow>
                <TableHeader>Document</TableHeader>
                <TableHeader>Site</TableHeader>
                <TableHeader>Rev</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Started</TableHeader>
                <TableHeader>Result</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((run, i) => (
                <TableRow key={`${run.document}-${run.started}-${i}`}>
                  <TableCell className={styles.documentCell}>
                    {run.status === 'Completed' ? (
                      <button
                        type="button"
                        className={styles.documentLink}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {run.document}
                      </button>
                    ) : (
                      run.document
                    )}
                  </TableCell>
                  <TableCell>{run.site}</TableCell>
                  <TableCell>{run.rev}</TableCell>
                  <TableCell>
                    <StatusTag status={run.status.toLowerCase()} />
                  </TableCell>
                  <TableCell className={styles.startedCell}>{run.started}</TableCell>
                  <TableCell className={styles.resultCell}>
                    {run.status === 'Completed' ? (
                      <>
                        <span className={styles.ready}>{run.ready} ready</span>
                        <span className={styles.resultSep}> / </span>
                        <span className={styles.sme}>{run.sme} SME</span>
                        <span className={styles.resultSep}> / </span>
                        <span className={styles.rejected}>{run.rejected} rejected</span>
                      </>
                    ) : run.status === 'Failed' ? (
                      <div className={styles.failedResult}>
                        <span>{run.message}</span>
                        <Button
                          kind="tertiary"
                          size="sm"
                          className={styles.retryButton}
                          onClick={() => handleRetry(i)}
                        >
                          Retry
                        </Button>
                      </div>
                    ) : (
                      <div className={styles.failedResult}>
                        <span>Extracting… {run.progress}%</span>
                        <Button
                          kind="tertiary"
                          size="sm"
                          className={styles.retryButton}
                          onClick={() => handleCancel(i)}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {selectedRun && <RunDetailPanel run={selectedRun} onClose={() => setSelectedIndex(null)} />}

      {toast && (
        <ToastNotification
          className={styles.toast}
          kind="info"
          title={toast}
          timeout={TOAST_TIMEOUT_MS}
          onClose={() => setToast(null)}
          lowContrast
        />
      )}
    </div>
  );
}
